// BlindsView.js - Estores Shelly, por RPC local
//
// O nome do ficheiro tem "View" porque `Blinds` é o ícone do lucide usado
// aqui dentro; sem isso os dois nomes colidiam.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Blinds, ArrowLeft, RefreshCw, ChevronUp, ChevronDown, Pause, AlertCircle,
} from 'lucide-react';

const API_URL = '/api';

// Todos os endpoints passaram a exigir autenticacao (antes alguns respondiam
// a qualquer pessoa, o que era o verdadeiro problema).
function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// Os estores movem-se devagar; 10s chega para acompanhar sem sondar de mais.
const INTERVALO_MS = 10000;

// Depois de mandar mover, esperar antes de reler: a posição só muda ao fim
// de um par de segundos e uma leitura imediata traria o valor antigo.
const ESPERA_APOS_COMANDO_MS = 2000;

const POSICOES_RAPIDAS = [0, 25, 50, 75, 100];

// Os estados que o Shelly reporta, com o emoji que os torna legíveis de
// relance numa grelha com muitos cartões.
const EMOJI_ESTADO = {
  open: '🔓', closed: '🔒', opening: '⬆️', closing: '⬇️', stopped: '⏸️',
};

function BlindsView({ onBack }) {
  const { t } = useTranslation();
  const [blinds, setBlinds] = useState([]);
  const [aMover, setAMover] = useState({});
  const [error, setError] = useState('');

  const fetchBlinds = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/shelly/devices`, { headers: authHeaders() });
      const data = await response.json();
      if (data.devices) setBlinds(data.devices);
    } catch (err) {
      setError(t('erros.buscarEstores'));
    }
  }, [t]);

  const controlar = async (blindId, action, position = null) => {
    setAMover((prev) => ({ ...prev, [blindId]: true }));
    try {
      const corpo = { action };
      if (position !== null) corpo.position = position;
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/shelly/${blindId}/control`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(corpo),
      });
      if (response.ok) setTimeout(fetchBlinds, ESPERA_APOS_COMANDO_MS);
    } catch (err) {
      setError(t('erros.controlarEstor'));
    } finally {
      setAMover((prev) => ({ ...prev, [blindId]: false }));
    }
  };

  useEffect(() => {
    fetchBlinds();
    const interval = setInterval(fetchBlinds, INTERVALO_MS);
    return () => clearInterval(interval);
  }, [fetchBlinds]);

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <button className="back-button" onClick={onBack}>
            <ArrowLeft size={20} />
            {t('comum.voltar')}
          </button>
          <div className="header-left">
            <Blinds size={32} />
            <div>
              <h1>{t('estores.titulo')}</h1>
              <p>{t('estores.subtitulo')}</p>
            </div>
          </div>
          <button onClick={fetchBlinds} className="btn-refresh">
            <RefreshCw size={16} />
            {t('comum.atualizar')}
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {blinds.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={64} className="spin" />
            <p>{t('estores.aCarregar')}</p>
          </div>
        ) : (
          <div
            className="devices-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}
          >
            {blinds.map((estor) => {
              const ocupado = !estor.online || aMover[estor.id];
              return (
                <div key={estor.id} className={`device-card blind-card ${estor.online ? 'active' : 'inactive'}`}>
                  <div className="device-header">
                    <div className="device-title">
                      <Blinds size={24} />
                      <div>
                        <h2>{estor.name}</h2>
                        <small>{estor.room}</small>
                        {!estor.online && <span className="offline-badge">{t('estores.offlineBadge')}</span>}
                      </div>
                    </div>
                    <div className="blind-status">
                      {estor.online && EMOJI_ESTADO[estor.state] && (
                        <span className={`status-badge status-${estor.state}`}>
                          {EMOJI_ESTADO[estor.state]} {t(`estores.estados.${estor.state}`)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="blind-position">
                    <div className="position-display">
                      <div className="position-label">{t('estores.posicaoAtual')}</div>
                      <div className="position-value">{estor.current_pos || 0}%</div>
                    </div>
                    <div className="position-bar">
                      <div className="position-fill" style={{ width: `${estor.current_pos || 0}%` }} />
                    </div>
                  </div>

                  <div className="blind-controls">
                    <button
                      onClick={() => controlar(estor.id, 'open')}
                      className="blind-btn blind-btn-open"
                      disabled={ocupado}
                    >
                      <ChevronUp size={20} />
                      {t('estores.abrir')}
                    </button>
                    <button
                      onClick={() => controlar(estor.id, 'stop')}
                      className="blind-btn blind-btn-stop"
                      disabled={ocupado}
                    >
                      <Pause size={20} />
                      {t('estores.parar')}
                    </button>
                    <button
                      onClick={() => controlar(estor.id, 'close')}
                      className="blind-btn blind-btn-close"
                      disabled={ocupado}
                    >
                      <ChevronDown size={20} />
                      {t('estores.fechar')}
                    </button>
                  </div>

                  <div className="blind-presets">
                    <div className="preset-label">{t('estores.posicoesRapidas')}</div>
                    <div className="preset-buttons">
                      {POSICOES_RAPIDAS.map((pos) => (
                        <button
                          key={pos}
                          onClick={() => controlar(estor.id, 'position', pos)}
                          className="preset-btn"
                          disabled={ocupado}
                        >
                          {pos}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {estor.online && (
                    <div className="blind-info">
                      <div className="info-row">
                        <span>{t('estores.consumo')}</span>
                        <span>{estor.power ? `${estor.power.toFixed(1)} W` : '0 W'}</span>
                      </div>
                      <div className="info-row">
                        <span>{t('estores.temperatura')}</span>
                        <span>{estor.temperature ? `${estor.temperature.toFixed(1)}°C` : 'N/A'}</span>
                      </div>
                      <div className="info-row">
                        <span>{t('estores.ip')}</span>
                        <span>{estor.ip}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default BlindsView;
